import { useCallback, useEffect, useRef, useState } from 'react';

const useInfiniteMovies = (fetcher) => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(Infinity);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const seenIds = useRef(new Set());
  const inFlight = useRef(false);
  const sentinelRef = useRef(null);

  const loadPage = useCallback(
    async (signal, pageToLoad) => {
      if (inFlight.current) return;
      if (pageToLoad > totalPages) return;
      inFlight.current = true;
      setLoading(true);
      setError(null);
      try {
        const response = await fetcher(pageToLoad, { signal });
        if (signal?.aborted) return;
        const fresh = (response.data.results || []).filter((m) => {
          if (seenIds.current.has(m.id)) return false;
          seenIds.current.add(m.id);
          return true;
        });
        setItems((prev) => [...prev, ...fresh]);
        if (response.data.total_pages) {
          setTotalPages(response.data.total_pages);
        }
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setError('Could not load movies. Please try again.');
      } finally {
        inFlight.current = false;
        setLoading(false);
      }
    },
    [fetcher, totalPages]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadPage(controller.signal, page);
    return () => controller.abort();
  }, [page, loadPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !inFlight.current &&
          page < totalPages
        ) {
          setPage((p) => p + 1);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [page, totalPages]);

  const retry = useCallback(() => {
    const controller = new AbortController();
    loadPage(controller.signal, page);
  }, [loadPage, page]);

  return { items, loading, error, sentinelRef, retry, hasMore: page < totalPages };
};

export default useInfiniteMovies;
